import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Upload, Download } from 'lucide-react'
import { Button, ConfirmDialog } from '@/components/ui'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { useSeedSample, type SeedEntityType } from '@/hooks/useSeedSample'

interface SetupGuideAffordancesProps {
  /** The entity type driving both the seed endpoint and the XLSX download filename. */
  entityType: SeedEntityType
  /** i18n namespace owning setup_guide.* keys (e.g. 'properties', 'customers'). */
  namespace: string
  /**
   * Whether to hide all affordances. Pass `true` when the entity already has
   * at least one item (count > 0). When hidden, the component renders null.
   */
  hidden?: boolean
}

/**
 * Renders 3 secondary/tertiary affordances for an empty list-page state:
 *   (a) Import — routes to the onboarding importer (AI-backed file upload)
 *   (b) Download sample — plain <a download> link to /import-samples/{entity}.xlsx
 *   (c) Seed sample data — opens ConfirmDialog then POSTs the seed endpoint
 *
 * Tier discipline (per Designer):
 *   - Seed sample: btn-outline (secondary weight, below the primary "New X" button)
 *   - Import + Download sample: ghost/text style (tertiary)
 */
export function SetupGuideAffordances({ entityType, namespace, hidden }: SetupGuideAffordancesProps) {
  const { t } = useTranslation(namespace)
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const addToast = useNotificationStore((s) => s.addToast)
  const seedSample = useSeedSample(entityType)
  const [showSeedConfirm, setShowSeedConfirm] = useState(false)

  if (hidden) return null

  const handleSeedConfirm = async () => {
    setShowSeedConfirm(false)
    try {
      const result = await seedSample.mutateAsync()
      if (result.seeded) {
        addToast({ type: 'success', message: t('setup_guide.seed_sample_success') })
      } else {
        // Backend no-op (this entity already exists) — don't claim a false success.
        addToast({ type: 'info', message: t('setup_guide.seed_sample_noop') })
      }
    } catch {
      addToast({ type: 'error', message: tc('errors.something_went_wrong') })
    }
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
        {/* Seed sample — secondary tier */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowSeedConfirm(true)}
          loading={seedSample.isPending}
          data-testid={`seed-sample-btn-${entityType}`}
        >
          {t('setup_guide.seed_sample')}
        </Button>

        {/* Import — tertiary tier */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          leftIcon={<Upload className="w-4 h-4" />}
          onClick={() => navigate('/properties/import')}
        >
          {t('setup_guide.import')}
        </Button>

        {/* Download sample — tertiary tier */}
        <Button
          as="a"
          href={`/import-samples/${entityType}.xlsx`}
          download
          variant="ghost"
          size="sm"
          leftIcon={<Download className="w-4 h-4" />}
        >
          {t('setup_guide.download_sample')}
        </Button>
      </div>

      {/* Seed confirm dialog */}
      <ConfirmDialog
        isOpen={showSeedConfirm}
        onClose={() => setShowSeedConfirm(false)}
        onConfirm={handleSeedConfirm}
        title={t('setup_guide.seed_sample')}
        message={t('setup_guide.seed_sample_confirm')}
        confirmLabel={tc('actions.confirm')}
        cancelLabel={tc('actions.cancel')}
        loading={seedSample.isPending}
      />
    </>
  )
}
